import { useCallback } from 'react'
import { NotificationLevel, notify } from '@retailer-platform/shared-components'
import { useStartImpersonation } from '../../../../../utils/impersonation/useStartImpersonation.hooks'
import { useNavigateTo } from '../../../../../exports/routing'
import { useTrackEvent } from '../../../../../exports/analytics'
import { useCurrentAccountContext } from '../../../../../exports/utils'
import { useImpersonationState } from '../../../../../exports/gin-and-tonic'

export const useAdminImpersonation = () => {
  const trackEvent = useTrackEvent()
  const navigateTo = useNavigateTo('base')
  const { account: currentAccount } = useCurrentAccountContext()
  const { impersonationData, setImpersonationData } = useImpersonationState()

  const [triggerMutation, { loading, error }] = useStartImpersonation()

  const startImpersonation = useCallback(
    async (impersonatedCanonicalId: string, targetAccountEmail: string) => {
      setImpersonationData({ ...impersonationData, pathOrigin: window.location.pathname })

      try {
        const result = await triggerMutation({
          variables: {
            impersonatedCanonicalId,
            breakglass: false,
          },
        })

        if (result.data?.enterpriseAccountEnableImpersonation?.success) {
          const trackResult = trackEvent({
            id: 'admin_controls.impersonate_user.start',
            description: 'Impersonate user session started from Admin Controls',
            data: {
              sourceUser: currentAccount.canonicalId,
              targetUser: impersonatedCanonicalId,
              targetEmail: targetAccountEmail,
              source: 'admin_controls',
            },
          })

          if (trackResult instanceof Promise) {
            await trackResult.finally(() => {
              navigateTo()
            })
          } else {
            navigateTo()
          }
        } else {
          notify({
            contents: 'Failed to start impersonation session',
            level: NotificationLevel.Error,
          })
        }
      } catch (err) {
        notify({
          contents: 'Error starting impersonation session',
          level: NotificationLevel.Error,
        })
      }
    },
    [
      triggerMutation,
      trackEvent,
      navigateTo,
      currentAccount,
      impersonationData,
      setImpersonationData,
    ]
  )

  return {
    startImpersonation,
    loading,
    error,
  }
}
